/**
 * Agentic Payment Airdrop - Shared Styles
 *
 * Centralized style objects for consistent UI across all components.
 * Using JavaScript objects for inline styles to maintain portability
 * and avoid CSS class name conflicts.
 */

// ============================================================================
// Color Palette
// ============================================================================

export const colors = {
  // Primary brand colors
  primary: "#2e3b61",
  primaryLight: "#3d4d7a",
  accent: "#e58734",
  accentDark: "#d77a2a",

  // Status colors
  success: "#28a745",
  successDark: "#218838",
  danger: "#dc3545",
  dangerDark: "#c82333",
  warning: "#ffc107",
  warningText: "#856404",
  warningBg: "#fff3cd",
  info: "#17a2b8",
  infoDark: "#138496",

  // Neutral colors
  white: "#fff",
  black: "#000",
  gray100: "#f8f9fa",
  gray200: "#f5f5f5",
  gray300: "#e0e0e0",
  gray400: "#ccc",
  gray500: "#999",
  gray600: "#666",
  gray700: "#333",

  // Backgrounds
  bgLight: "#fafafa",
  bgMedium: "#f0f0f0",

  // Borders
  border: "#e0e0e0",
  borderLight: "#ddd",
};

// ============================================================================
// Gradients
// ============================================================================

export const gradients = {
  primary: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
  accent: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
  success: `linear-gradient(135deg, ${colors.success} 0%, ${colors.successDark} 100%)`,
  danger: `linear-gradient(135deg, ${colors.danger} 0%, ${colors.dangerDark} 100%)`,
  info: `linear-gradient(135deg, ${colors.info} 0%, ${colors.infoDark} 100%)`,
  gray: `linear-gradient(135deg, #6c757d 0%, #5a6268 100%)`,
};

// ============================================================================
// Common Styles
// ============================================================================

export const pageHeaderStyles = {
  container: {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
    padding: "28px 32px",
    borderRadius: "8px 8px 0 0",
    marginBottom: "0",
  },
  title: {
    margin: "0 0 12px 0",
    fontSize: "28px",
    fontWeight: 700,
    color: colors.white,
    letterSpacing: "0.3px",
  },
  infoBox: {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    background: "rgba(255,255,255,0.12)",
    padding: "10px 18px",
    borderRadius: "6px",
    border: "1px solid rgba(255,255,255,0.2)",
  },
  infoLabel: {
    fontSize: "13px",
    fontWeight: 500,
    color: "rgba(255,255,255,0.8)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  infoValue: {
    fontSize: "15px",
    fontWeight: 600,
    color: colors.white,
    background: `rgba(229, 135, 52, 0.3)`,
    padding: "4px 10px",
    borderRadius: "4px",
    border: `1px solid rgba(229, 135, 52, 0.5)`,
  },
};

export const commonStyles = {
  // Panel/Card containers
  panel: {
    background: colors.white,
    borderRadius: "6px",
    border: `1px solid ${colors.border}`,
    overflow: "hidden",
  },

  // Panel headers
  panelHeader: {
    margin: 0,
    padding: "16px 20px",
    background: gradients.primary,
    color: colors.white,
    fontSize: "16px",
    fontWeight: 600,
    borderBottom: `2px solid ${colors.accent}`,
  },

  // Section headers (larger)
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    background: gradients.primary,
    borderRadius: "6px 6px 0 0",
    marginBottom: "20px",
    borderBottom: `3px solid ${colors.accent}`,
  },

  // Form inputs
  input: {
    width: "100%",
    padding: "10px 14px",
    border: `1px solid ${colors.gray400}`,
    borderRadius: "4px",
    fontSize: "14px",
    boxSizing: "border-box",
  },

  // Buttons
  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 20px",
    border: "none",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  buttonPrimary: {
    background: gradients.primary,
    color: colors.white,
  },

  buttonAccent: {
    background: gradients.accent,
    color: colors.white,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },

  buttonDanger: {
    background: gradients.danger,
    color: colors.white,
  },

  buttonOutline: {
    background: colors.white,
    color: colors.primary,
    border: `1px solid ${colors.primary}`,
  },

  buttonDisabled: {
    background: colors.gray300,
    color: colors.gray500,
    cursor: "not-allowed",
  },

  buttonGhost: {
    background: "rgba(255,255,255,0.15)",
    color: colors.white,
    border: "1px solid rgba(255,255,255,0.3)",
  },

  // Filter/Search bar
  filterBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 20px",
    background: colors.gray100,
    borderRadius: "6px",
    border: `1px solid ${colors.border}`,
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "12px",
  },

  // Badge/Pill
  badge: {
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: 600,
  },

  badgeActive: {
    background: colors.accent,
    color: colors.white,
  },

  badgeInactive: {
    background: "#6c757d",
    color: colors.white,
  },

  // Warning box
  warningBox: {
    padding: "14px 16px",
    background: colors.warningBg,
    border: `1px solid ${colors.warning}`,
    borderRadius: "4px",
    fontSize: "13px",
    color: colors.warningText,
  },

  // Code/monospace text
  code: {
    background: colors.gray200,
    padding: "4px 8px",
    borderRadius: "3px",
    fontSize: "12px",
    fontFamily: "monospace",
  },

  // Table header cell
  tableHeaderCell: {
    padding: "14px 16px",
    textAlign: "left",
    color: colors.white,
    fontWeight: 600,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  // Table body cell
  tableCell: {
    padding: "12px 16px",
    borderBottom: `1px solid ${colors.border}`,
  },

  // Labels
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: 600,
    color: colors.primary,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
};

// ============================================================================
// Stats Card Styles
// ============================================================================

export const statsCardStyles = {
  container: {
    display: "flex",
    gap: "16px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },

  card: {
    flex: 1,
    minWidth: "140px",
    padding: "16px 20px",
    borderRadius: "6px",
    textAlign: "center",
  },

  value: {
    display: "block",
    fontSize: "28px",
    fontWeight: 700,
    color: colors.white,
  },

  label: {
    fontSize: "12px",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.85)",
    fontWeight: 600,
    letterSpacing: "0.5px",
  },
};

// ============================================================================
// Pagination Styles
// ============================================================================

export const paginationStyles = {
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 16px",
    background: colors.gray200,
    borderTop: `1px solid ${colors.border}`,
    flexWrap: "wrap",
    gap: "12px",
  },

  info: {
    fontSize: "13px",
    color: colors.gray600,
  },

  nav: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },

  navButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 14px",
    height: "36px",
    fontSize: "14px",
    fontWeight: 600,
    border: "none",
    borderRadius: "4px",
    userSelect: "none",
  },

  pageButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "36px",
    height: "36px",
    padding: "0 8px",
    fontSize: "14px",
    borderRadius: "4px",
    userSelect: "none",
  },
};

// ============================================================================
// Modal Styles
// ============================================================================

export const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },

  content: {
    background: colors.white,
    borderRadius: "8px",
    maxWidth: "500px",
    width: "90%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    overflow: "hidden",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: `3px solid ${colors.dangerDark}`,
  },

  body: {
    padding: "20px",
  },

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "16px 20px",
    background: colors.gray100,
    borderTop: `1px solid ${colors.border}`,
  },
};

// ============================================================================
// Loading State Styles
// ============================================================================

export const loadingStyles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    background: colors.gray100,
    borderRadius: "6px",
    border: `1px solid ${colors.border}`,
  },

  spinner: {
    width: "40px",
    height: "40px",
    border: `3px solid ${colors.border}`,
    borderTopColor: colors.primary,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    marginBottom: "16px",
  },

  text: {
    color: colors.gray600,
    fontSize: "14px",
  },
};

// ============================================================================
// Empty State Styles
// ============================================================================

export const emptyStateStyles = {
  container: {
    padding: "60px 20px",
    background: colors.gray100,
    border: `1px solid ${colors.border}`,
    borderRadius: "0 0 6px 6px",
    textAlign: "center",
  },

  text: {
    margin: 0,
    color: colors.gray600,
    fontSize: "14px",
  },
};

// ============================================================================
// Tab Styles
// ============================================================================

export const tabStyles = {
  container: {
    display: "flex",
    gap: 0,
    background: colors.bgMedium,
    padding: 0,
    borderBottom: `3px solid ${colors.accent}`,
  },

  tab: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px 28px",
    fontSize: "14px",
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  tabActive: {
    color: colors.white,
    background: colors.primary,
  },

  tabInactive: {
    color: colors.primary,
    background: "transparent",
  },
};

// ============================================================================
// Keyframe Animation (as string for injection)
// ============================================================================

export const spinnerKeyframes = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
